<template>
  <td class="hrmThemeTableTd hrmThemeTableEmployeeCol">
    <div class="flex items-center justify-start gap-3">
      <div class="flex flex-col gap-1" v-if="employeeInfo">
        <div class="flex items-center gap-1">
          <div class="relative w-[50px] h-[50px]">

            <!-- Doughnut Chart -->
            <Doughnut v-if="showProfileCompletion" :data="attendanceData" :options="options"
              style="width: 100px; height: 100px" />

            <!-- Centered Profile Image -->
            <img loading="lazy"
              :src="employeeInfo?.profile_photo || employeeInfo?.profile_photo_url || defaultAvatarUrl"
              class="absolute inset-0 m-auto w-9 h-9 rounded-full"
              :class="showProfileCompletion ? 'showProfileCompletion' : ''" />
            <div class="hidden showProfileInfo hrm_button_emp_tooltip hrm_tooltip_right text-left">
              <p>
                {{ `Completed ${profileProgress || 0}%` }}
              </p>
              <p>
                {{ `Remaining ${100 - profileProgress || 0}%` }}
              </p>
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <div class="flex flex-wrap gap-1">
              <template v-if="permissionPreview && employeeInfo?.id">
                <inertia-link class="hrm_menu_item_inner hover:text-hrmThemeBgColor" :href="route(
                  'employee-management.employees-preview',
                  employeeInfo?.id
                )

                  ">
                  <div class="w-24 md:w-auto md:flex-1 md:min-w-0 truncate">

                    {{ employeeInfo?.user?.name || employeeInfo?.name }}
                  </div>
                </inertia-link>
              </template>
              <template v-else>
                <div class="w-24 md:w-auto md:flex-1 md:min-w-0 truncate">

                  {{ employeeInfo?.user?.name || employeeInfo?.name }}
                </div>
              </template>
              <div class="flex items-center justify-start">
                <span v-if="employeeInfo"
                  class="flex gap-1 items-center text-hrmThemeTextColor bg-hrmThemeBgColor font-bold text-xs px-2 py-0.5 rounded-sm hrm_employee_code_tag">
                  {{ employeeInfo?.employee_code || employeeInfo?.employee_id }}
                </span>

                <div class="ml-2 flex gap-3">
                  <template v-if="employeeInfo?.employee_have_permission">
                    <hrm-button buttonMode="iconSpanButton" iconSize="16px" buttonPadding="0" buttonSize="20px"
                      buttonClass="flex justify-center items-center" buttonAlign="center"
                      icon="solar:key-minimalistic-square-broken" tooltipText="Employee Permission"
                      tooltipPosition="top" />
                  </template>
                  <template v-if="employeeInfo?.is_lock">
                    <hrm-button buttonMode="iconSpanButton" iconSize="16px" buttonPadding="0" buttonSize="18px"
                      buttonClass="flex justify-center items-center" buttonAlign="center" icon="si:lock-line"
                      tooltipText="Profile Locked" tooltipPosition="top" />
                  </template>
                </div>
              </div>
              <span v-if="employeeInfo?.status == 'separated'"
                class="flex gap-1 items-center text-hrmThemeTextColor bg-red-500 font-bold px-2 py-0.5 rounded-sm text-xxs">
                {{ trans("lang.labels.separated") }}
              </span>
            </div>
            <div class="text-sm flex-col items-center justify-center">
              <div class="flex flex-col items-start gap-0.5"
                v-if="employeeInfo?.designation || employeeInfo?.department">
                <span class="w-32 md:w-auto md:flex-1 md:min-w-0 truncate">{{ employeeInfo?.designation?.name }}</span>
                <p class="w-48 md:w-auto md:flex-1 md:min-w-0 truncate"
                  v-if="employeeInfo?.department?.department_name">
                  ({{ employeeInfo?.department?.department_name }})
                </p>
              </div>
              <div class="flex items-center gap-0.5">
                <span class="flex gap-0.5">
                  <template v-if="locationId">
                    <hrm-button v-if="employeeInfo?.location_head" buttonMode="iconSpanButton" iconSize="16px"
                      buttonPadding="0" buttonSize="20px" buttonClass="flex justify-center items-center"
                      buttonAlign="center" icon="solar:users-group-rounded-broken" tooltipText="Location Head"
                      tooltipPosition="top" />
                    <hrm-button v-if="employeeInfo?.location_assign" buttonMode="iconSpanButton" iconSize="16px"
                      buttonPadding="0" buttonSize="20px" buttonClass="flex justify-center items-center"
                      buttonAlign="center" icon="ri:user-location-fill" tooltipText="Location Assign"
                      tooltipPosition="top" />
                  </template>
                  <template v-if="departmentId">
                    <hrm-button v-if="employeeInfo?.department_head" buttonMode="iconSpanButton" iconSize="16px"
                      buttonPadding="0" buttonSize="20px" buttonClass="flex justify-center items-center"
                      buttonAlign="center" icon="solar:shield-user-broken" tooltipText="Department Head"
                      tooltipPosition="top" />
                    <hrm-button v-if="employeeInfo?.department_assign" buttonMode="iconSpanButton" iconSize="16px"
                      buttonPadding="0" buttonSize="20px" buttonClass="flex justify-center items-center"
                      buttonAlign="center" icon="solar:buildings-broken" tooltipText="Department Assign"
                      tooltipPosition="top" />
                  </template>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template v-else> No Employee </template>
    </div>
  </td>
</template>

<script setup>
import { onMounted, ref, computed, inject } from "vue";
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js";
import { Doughnut } from "vue-chartjs";
import { trans } from "@/helpers";

ChartJS.register(ArcElement, Tooltip);

const profileProgress = ref(0); // start with 0

// compute default avatar URL
const defaultAvatarUrl = computed(() => {
  const employeeName = employeeInfo.value?.user?.name || employeeInfo.value?.name || 'Employee';
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(employeeName)}&color=7F9CF5&background=EBF4FF`;
});

// reactive chart data
const attendanceData = computed(() => ({
  labels: ["Completed", "Remaining"],
  datasets: [
    {
      data: [
        profileProgress.value || 0,
        100 - (profileProgress.value || 0)
      ],
      backgroundColor: ["#3B82F6", "#E5E7EB"],
      borderWidth: 0,
    },
  ],
}));

const options = {
  cutout: "90%",
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: false, // disable default internal tooltip
      external: function (context) {
        // Tooltip element
        let tooltipEl = document.getElementById('chartjs-tooltip');

        // Create element if it doesn't exist
        if (!tooltipEl) {
          tooltipEl = document.createElement('div');
          tooltipEl.id = 'chartjs-tooltip';
          tooltipEl.style.position = 'absolute';
          tooltipEl.style.background = '#333';
          tooltipEl.style.color = '#fff';
          tooltipEl.style.padding = '4px 8px';
          tooltipEl.style.borderRadius = '4px';
          tooltipEl.style.pointerEvents = 'none';
          tooltipEl.style.transition = 'all 0.1s ease';
          tooltipEl.style.fontSize = '12px';
          document.body.appendChild(tooltipEl);
        }

        // Hide if no tooltip
        const tooltipModel = context.tooltip;
        if (tooltipModel.opacity === 0) {
          tooltipEl.style.opacity = 0;
          return;
        }

        // Build tooltip HTML with labels
        if (tooltipModel.dataPoints) {
          const dataPoint = tooltipModel.dataPoints[0];
          tooltipEl.innerHTML = `
            <strong>${dataPoint.label}</strong>: ${dataPoint.formattedValue}%
          `;
        }

        // Position tooltip outside chart
        const canvasRect = context.chart.canvas.getBoundingClientRect();
        tooltipEl.style.opacity = 1;
        tooltipEl.style.left =
          canvasRect.left + window.pageXOffset + tooltipModel.caretX + 'px';
        tooltipEl.style.top =
          canvasRect.top + window.pageYOffset + tooltipModel.caretY - 30 + 'px';
      }
    }
  },
};


const props = defineProps([
  "rowData",
  "employeeData",
  "permissionPreview",
  "locationId",
  "departmentId",
  "queryData",
  "viewTypeEmployeeComp",
  "tableKey",
]);

const employeeInfo = ref();
const showProfileCompletion = inject("showProfileCompletion", false);

onMounted(() => {
  if (props.viewTypeEmployeeComp == "EmpCol") {
    employeeInfo.value = props.rowData.employee;
  } else if (props.viewTypeEmployeeComp == "EmpComplaintCol") {
    if (props.tableKey == "complaintFrom") {
      employeeInfo.value = props.rowData.complaintFrom;
    } else {
      employeeInfo.value = props.rowData.complaintAgainst;
    }
  } else if (props.viewTypeEmployeeComp == "EmpTravelDetails") {
    employeeInfo.value = props.rowData.employee_travel_detail;
  } else if (props.viewTypeEmployeeComp == "SeparationProcess") {
    employeeInfo.value = props.rowData?.separation?.employee;
  } else {
    if (props.employeeData?.name && props.employeeData?.employee_code) {
      employeeInfo.value = props.employeeData;
    } else {
      employeeInfo.value = props.rowData;
    }
  }

  profileProgress.value =
    employeeInfo.value?.profileCompletionPercentage?.percentage || 0;
});
</script>

<style>
.user_project_media_action {
  display: none;
}

.user_project_media:hover .user_project_media_action {
  display: flex;
}

.carousel_slideImg {
  height: 500px;
}

.linkMediaClick {
  background-color: var(--hrm_Theme_Bg_Color);
  color: var(--hrm_Theme_Text_Color);
}

.linkMediaClick:hover {
  opacity: 0.6;
}

.hrm_employee_code_tag {
  font-size: 9px;
  /* padding: 1px 2px; */
}

.showProfileCompletion:hover+.showProfileInfo {
  display: block;
  visibility: visible;
  opacity: 1;
}

.hrm_tooltip_right {
  top: 50%;
  left: 136%;
  transform: translateY(-50%);
}

.hrm_tooltip_right::after {
  top: 50%;
  left: -4px;
  transform: translate(-50%, -50%);
  margin-top: 0;
  border-width: 8px 8px 8px 0;
  border-color: transparent #2f3336 transparent transparent;
}

.hrm_button_emp_tooltip {
  position: absolute;
  visibility: hidden;
  background-color: #2f3336;
  color: #fff;
  text-align: start;
  padding: 5px 10px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 9999999999;
  opacity: 0;
  transition: opacity 0.3s !important;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
}

.text-xxs {
  font-size: 9px;
}
</style>